import * as Sentry from '@sentry/browser'
import Sortable from 'sortablejs'
import { v4 as uuidv4 } from 'uuid'
import {
  focusRichTextEditor,
  reconcileRichTextInlineMedia,
  syncRichTextEditor,
  updateRichTextEditor
} from '../../../vendor/lacodix/resourcerer-rich-text/resources/js/rich-text.js'
import tabs from './tabs.js'

// The one rich-text instance of this editor: it travels into the point that is
// being written and is parked outside the list the rest of the time.
const POINT_CONTENT_EDITOR_ID = 'meeting-point-content'
/** Query parameter that keeps the open section across a reload. */
const TAB_PARAM = 'tab'
// Server methods that answer with skipRender(): they never morph the agenda.
const MORPHLESS_CALLS = ['saveItemField', 'loadPointHistory', 'savePointResponsibilities', 'newPointTaskIdentity']
// Idle pause before typed minutes are stored on their own, in milliseconds.
const INLINE_IDLE_MS = 10000
// How large the beamer stage sets its type, remembered per browser: it depends on
// the room and the projector, not on the meeting.
const PRESENTATION_SCALE_KEY = 'meeting-presentation-font-scale'
// Whether the agenda stands beside the stage, remembered per browser for the same
// reason the type size is: it depends on the screen in the room.
const PRESENTATION_SIDEBAR_KEY = 'meeting-presentation-agenda-open'
// Below this the stage would have to share the screen with the list, so the list
// starts closed and is opened by hand. Tailwind's `xl`: on anything smaller the
// point itself is what the room came to read.
const PRESENTATION_SIDEBAR_MIN_WIDTH = 1280
const PRESENTATION_SCALE_STEP = 0.1
const PRESENTATION_SCALE_MIN = 0.7
const PRESENTATION_SCALE_MAX = 2

/**
 * Local interaction state for the meeting editor plus its optional drag-and-drop
 * enhancement. The Livewire proxy is always resolved afresh from a stored wire:id;
 * only complete occurrence-ID orders are sent to the server.
 */
export default ({
  initialTab = 'agenda',
  errorTitle = 'Error',
  errorMessage = 'The change could not be saved.',
  // "Point :current of :total" - without a list around it the stage is the only
  // place that can say where the meeting stands.
  positionLabel = ':current / :total'
} = {}) => {
  let wireId = null
  let sortable = null
  let orderBeforeDrag = []
  let stopWatchingCommits = null
  let inlineContentTimer = null
  let inlineContentDirty = false
  let inlineContentRevision = 0
  let inlineContentRequestSequence = 0
  let latestInlineContentRequestId = 0
  let inlineSaveRequestCount = 0
  let inlineTokenSave = null
  let pendingReopenPointId = null
  let pendingReopenContent = null
  // What the beamer showed while a morph was on its way, put back with the editor.
  let pendingPresentingPointId = null
  // The agenda as it stood before that morph. A point the request removed is gone
  // from the DOM afterwards, so where it stood can only be read from here.
  let pendingAgendaOrder = []
  let stopGuardingEdits = null
  let stopWatchingFullscreen = null
  // The one click the guard lets through: its own replay, remembered by identity.
  let replayedClick = null
  // The component root, resolved once while init() is still running on it.
  let rootElement = null
  const tabNavigation = tabs({ initialTab })

  return {
    ...tabNavigation,
    sorting: false,
    // Inline point editing: exactly one rich-text instance travels to the point
    // that is being worked on; every other point stays plain rendered markup.
    inlinePointId: null,
    pointTask: { token: '', source: '', title: '', people: [], due: '', mode: 'pending' },
    selectedPointTaskToken: '',
    pointTaskCanCreate: false,
    pointTaskDialogOpen: false,
    pointTaskPeopleError: false,
    pointTaskOccurrenceId: null,
    pointTaskBusy: false,
    inlineSaving: false,
    inlineSavedFlash: false,
    // Still virtual open points are addressed by their point identity, never by an
    // occurrence id - they do not have one until this meeting adopts them.
    adoptingItemId: null,
    // Beamer view: the occurrence the overlay is showing, null when it is closed.
    // The overlay offers the very same content slot a card does, so presenting is
    // nothing but the travelling editor moving to another surface.
    presentingPointId: null,
    // Whether a point is currently being added from the stage.
    stageAddingPoint: false,
    // Whether THIS overlay asked for real fullscreen and got it. A browser may
    // refuse, and then the overlay is all there is.
    presentationFullscreen: false,
    // Type size of the beamer stage, 1 being what the stage was designed at.
    presentationFontScale: 1,
    // Whether the agenda stands beside the stage.
    presentationSidebarOpen: false,
    positionLabel,
    // Where the stage stands, in words. Kept as state rather than computed in the
    // markup: the order is read out of the DOM, and Alpine cannot watch that - an
    // expression would keep showing "point 2 of 5" after a move or a deletion.
    presentationPositionLabel: '',
    // Reactive mirror of that same order, for the controls that have to follow it
    // (the page-turn buttons at the ends of the agenda).
    agendaOrder: [],
    errorTitle,
    errorMessage,

    init() {
      rootElement = this.$root ?? null
      const root = rootElement?.closest?.('[wire\\:id]')
      wireId = root ? root.getAttribute('wire:id') : null
      this.syncTabs()
      this.restoreTabFromLocation()
      this.presentationFontScale = this.storedPresentationFontScale()
      this.$nextTick(() => {
        this.syncTabs()
        this.watchLivewireCommits()
        this.guardPendingEdits()
        this.watchFullscreenExit()
        this.bootAgenda()
        this.syncAgendaOrder()
      })
    },

    destroy() {
      this.closePointTask({ restoreFocus: false })
      stopWatchingCommits?.()
      stopWatchingCommits = null
      stopGuardingEdits?.()
      stopGuardingEdits = null
      stopWatchingFullscreen?.()
      stopWatchingFullscreen = null
      pendingPresentingPointId = null
      pendingAgendaOrder = []
      replayedClick = null
      this.presentingPointId = null
      this.restorePointTools()
      this.syncPresentationChrome()
      clearTimeout(inlineContentTimer)
      inlineContentTimer = null
      inlineContentDirty = false
      latestInlineContentRequestId = ++inlineContentRequestSequence
      inlineTokenSave = null
      pendingReopenPointId = null
      pendingReopenContent = null
      this.destroyAgenda()
      rootElement = null
    },

    /**
     * The editor root and the single travelling rich-text instance, resolved
     * without Alpine's element bound magics.
     *
     * `$root` and `$refs` are evaluated against the element whose expression is
     * running. A control that the very request it started removes from the DOM -
     * an open point that has just been taken over, a card a morph replaced -
     * therefore hands back `undefined` in every callback that runs afterwards,
     * and the travelling editor is never moved into its point.
     */
    editorRoot() {
      return rootElement ?? document
    },

    editorRef(name) {
      return this.editorRoot().querySelector(`[x-ref="${name}"]`)
    },

    /**
     * The one travelling rich-text instance, wherever it currently is. Written in
     * a card it sits inside the component root; shown on the beamer it sits in the
     * overlay, which hangs on `body` and is therefore outside that root - looking
     * it up through `editorRef()` alone would hand back null exactly then, and
     * neither the click guard nor the parking before a morph would find it.
     */
    pointEditorElement() {
      return this.editorRef('pointEditor') ?? document.querySelector('[x-ref="pointEditor"]')
    },

    /**
     * Tell the page whether the beamer view is up.
     *
     * Only while it is, the action dialog is lifted over the overlay (app.scss).
     * Raising it for good would push it over the teleported dropdowns of forms
     * that live INSIDE a dialog, which sit on the same layer and only stay on top
     * by coming later in the document.
     */
    syncPresentationChrome() {
      document.body?.classList?.toggle('meeting-presenting', this.presentingPointId !== null)
    },

    /** The teleported beamer overlay; null before Alpine ever rendered it. */
    presentationRoot() {
      return document.querySelector('[data-meeting-presentation]')
    },

    /**
     * The sidebar is deliberately not another editable copy of the point. It is
     * static Blade markup, so a morphless title save has to move the accepted
     * wording into it explicitly, together with the full-text tooltip.
     */
    syncPresentationAgendaTitle(detail = {}) {
      if (
        !Number.isInteger(detail.occurrenceId) ||
        detail.field !== 'title' ||
        typeof detail.value !== 'string'
      ) return

      const entry = Array.from(
        this.presentationRoot()?.querySelectorAll('[data-agenda-entry-occurrence-id]') ?? []
      ).find(candidate => Number.parseInt(candidate.dataset.agendaEntryOccurrenceId, 10) === detail.occurrenceId)
      const title = entry?.querySelector('[data-presentation-agenda-title]')

      if (!title) return

      title.textContent = detail.value
      title.setAttribute('title', detail.value)
    },

    /**
     * The controls Livewire materialised once for each point. A group can be in
     * its agenda home or in the overlay, which lives outside the Livewire root;
     * looking on both surfaces is therefore part of the DOM contract.
     */
    pointToolGroups() {
      return [...new Set(this.pointSurfaces().flatMap(surface => Array.from(
        surface.querySelectorAll('[data-point-tool-group][data-point-tool-occurrence]')
      )))]
    },

    /**
     * Put every materialised toolbar back where Livewire rendered its teleport
     * anchor. This always runs before a morph: morphing a node that currently
     * hangs in the body overlay would otherwise destroy it without re-creating it.
     */
    restorePointTools() {
      const root = this.editorRoot()

      this.pointToolGroups().forEach(group => {
        const kind = group.dataset.pointToolGroup
        const occurrenceId = Number.parseInt(group.dataset.pointToolOccurrence, 10)
        if (!kind || !Number.isInteger(occurrenceId)) return

        const home = root.querySelector(`[data-point-tool-home="${kind}:${occurrenceId}"]`)
        if (home && group.parentElement !== home) home.appendChild(group)
      })
    },

    /**
     * Lift exactly the presented point's existing groups into the empty stage
     * destinations. Starting with a restore also returns the previous point's
     * controls when the presenter pages through the agenda.
     */
    syncPresentedPointTools() {
      this.restorePointTools()
      if (this.presentingPointId === null) return

      const overlay = this.presentationRoot()
      const root = this.editorRoot()
      if (!overlay) return

      for (const kind of ['move', 'actions']) {
        const group = root.querySelector(
          `[data-point-tool-group="${kind}"][data-point-tool-occurrence="${this.presentingPointId}"]`
        )
        const target = overlay.querySelector(`[data-presentation-point-tool-target="${kind}"]`)

        if (group && target) target.appendChild(group)
      }
    },

    /**
     * Where the editor belongs for that point. The card and the beamer overlay
     * offer the very same slot contract, so the only open question is which of
     * the two surfaces is in charge of the point right now.
     */
    pointContentSlot(occurrenceId) {
      const root = this.presentingPointId === occurrenceId
        ? this.presentationRoot()
        : this.editorRoot()

      return root?.querySelector(`[data-point-content-slot="${occurrenceId}"]`) ?? null
    },

    /**
     * Both surfaces that render a point: the agenda list inside the component
     * root and the beamer stage, which hangs on `body`.
     */
    pointSurfaces() {
      const surfaces = [this.editorRoot()]
      const overlay = this.presentationRoot()

      if (overlay) surfaces.push(overlay)

      return surfaces
    },

    /**
     * Every node of that kind, on BOTH surfaces. The same point is on screen
     * twice, and an inline save skips the morph on purpose - so whatever the
     * browser repaints itself has to be repainted in the list AND on the stage,
     * or the one that was not used shows a value the server no longer holds.
     */
    pointNodes(selector) {
      return this.pointSurfaces().flatMap(root => Array.from(root.querySelectorAll(selector)))
    },

    /** Whether that point still has a card in the agenda list. */
    pointOnAgenda(occurrenceId) {
      return occurrenceId !== null &&
        this.editorRoot().querySelector(`[data-point-content-slot="${occurrenceId}"]`) !== null
    },

    selectTab(tab) {
      if (!this.tabs.includes(tab)) return

      this.tab = tab
      this.rememberTab(tab)
      this.$nextTick(() => {
        if (tab === 'agenda') this.bootAgenda()
      })
    },

    /**
     * Which section is open belongs in the address, not only in this component:
     * a reload, a shared link and the back button then all land where the reader
     * was. Only tabs this editor actually renders are accepted - a meeting that
     * shows no participants tab must not open one because a URL says so.
     */
    restoreTabFromLocation() {
      const requested = new URLSearchParams(window.location.search).get(TAB_PARAM)

      if (requested && this.tabs.includes(requested)) this.tab = requested
    },

    /**
     * Replace rather than push: the tabs of one editor are one place, not a trail
     * of pages to step back through. The first tab drops the parameter again so a
     * plain editor link stays plain.
     */
    rememberTab(tab) {
      const url = new URL(window.location.href)

      if (tab === this.tabs[0]) {
        url.searchParams.delete(TAB_PARAM)
      } else {
        url.searchParams.set(TAB_PARAM, tab)
      }

      window.history.replaceState(window.history.state, '', url)
    },

    showError(detail = {}) {
      // A server section can name a panel that is no longer rendered (recipients
      // moved into the participants tab); never switch to a panel that is gone.
      // Through selectTab, never by assigning `tab`: one place decides what a tab
      // switch entails, so a switch the server asked for behaves exactly like one
      // the reader made.
      const section = detail.section
      if (this.tabs.includes(section)) {
        this.selectTab(section)
      } else if (section === 'recipients' && this.tabs.includes('participants')) {
        this.selectTab('participants')
      }

      // Validation effects may arrive while wire:loading still makes the form
      // inert. Keep focus deferred so Livewire can clear that state first.
      this.$nextTick(() => {
        const target = this.editorRoot().querySelector(`[data-error-section="${section}"]`)
        target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
        const invalid = target?.querySelector?.('[aria-invalid="true"]') ||
          target?.querySelector?.('input, select, textarea, button')

        invalid?.focus?.({ preventScroll: true })
      })
    },

    refreshAgenda() {
      this.$nextTick(() => {
        this.bootAgenda()
        this.syncAgendaOrder()
      })
    },

    /**
     * Take the order the DOM currently shows into reactive state. Everything that
     * can move a point calls this: a drag, the arrows, a deletion, a morph.
     */
    syncAgendaOrder() {
      this.agendaOrder = this.completeOrder()
      this.presentationPositionLabel = this.presentationPosition()
      this.syncPresentedPointTools()
    },

    restoreParticipantResolutionFocus() {
      this.$nextTick(() => {
        this.editorRoot().querySelector('[data-testid="resolve-participants"]')?.focus?.()
      })
    },

    async cancelParticipantResolutionDiscard() {
      const wire = this._livewire()

      if (!wire) {
        this.reportError(new Error('meetingEditor: Livewire component not resolvable'))
        return false
      }

      try {
        await wire.cancelParticipantResolutionDiscard()

        return true
      } catch (error) {
        this.reportError(error)

        return false
      }
    },

    _livewire() {
      return wireId && window.Livewire ? (window.Livewire.find(wireId) || null) : null
    },

    /**
     * Open the confirmation dialog that removes a point from this meeting.
     *
     * The click already passed the capture phase guard, which stored whatever was
     * typed into the travelling editor; the explicit flush here is the second
     * layer for a call that never came from a click at all.
     */
    async removePoint(action, occurrenceId) {
      if (!['confirmDeleteItem', 'confirmResetItem'].includes(action)) return false

      if (await this.flushInlineContent() === false) return false

      const wire = this._livewire()

      if (!wire || typeof wire[action] !== 'function') {
        this.reportError(new Error('meetingEditor: Livewire component not resolvable'))

        return false
      }

      try {
        await wire[action](occurrenceId)

        return true
      } catch (error) {
        this.reportError(error)

        return false
      }
    },

    /** Open the destructive dialog for one attachment after pending minutes settle. */
    async removeAttachment(occurrenceId, attachmentId) {
      if (await this.flushInlineContent() === false) return false

      const wire = this._livewire()
      if (!wire || typeof wire.confirmDeleteAttachment !== 'function') {
        this.reportError(new Error('meetingEditor: Livewire component not resolvable'))

        return false
      }

      try {
        await wire.confirmDeleteAttachment(occurrenceId, attachmentId)

        return true
      } catch (error) {
        this.reportError(error)

        return false
      }
    },

    /** Open the point's one FilePond dialog after pending minutes settle. */
    async openAttachmentUpload(occurrenceId) {
      if (await this.flushInlineContent() === false) return false

      const wire = this._livewire()
      if (!wire || typeof wire.prepareAttachmentUpload !== 'function') {
        this.reportError(new Error('meetingEditor: Livewire component not resolvable'))

        return false
      }

      try {
        return await wire.prepareAttachmentUpload(occurrenceId)
      } catch (error) {
        this.reportError(error)

        return false
      }
    },

    destroyAgenda() {
      if (!sortable) return

      try {
        sortable.destroy()
      } catch {
        // Livewire may already have replaced the Sortable-owned element.
      }

      sortable = null
    },

    bootAgenda() {
      this.destroyAgenda()
      const list = this.$refs?.agendaList
      if (!list || list.querySelectorAll('[data-meeting-item]').length < 2) return

      sortable = new Sortable(list, {
        draggable: '[data-meeting-item]',
        handle: '[data-meeting-drag-handle]',
        dataIdAttr: 'data-occurrence-id',
        animation: 160,
        onStart: () => { orderBeforeDrag = this.completeOrder() },
        onEnd: () => { this.persistOrder() }
      })
    },

    /**
     * Keyboard/touch equivalent of a drag: the swap happens in the DOM and the
     * complete order is persisted exactly like Sortable does on drop.
     */
    movePoint(occurrenceId, offset) {
      const list = this.agendaListElement()
      if (!list) return

      const items = Array.from(list.querySelectorAll('[data-meeting-item]'))
      const index = items.findIndex(element => Number.parseInt(element.dataset.occurrenceId, 10) === occurrenceId)
      const target = index + offset

      if (index === -1 || target < 0 || target >= items.length) return

      orderBeforeDrag = this.completeOrder()

      if (offset < 0) items[target].before(items[index])
      else items[target].after(items[index])

      this.refreshMoveControls()
      this.persistOrder()
    },

    /**
     * Keep the edge arrows disabled at the new edges until the server answers -
     * on the stage as well as in the list, which is why the arrows are addressed
     * by the point they move rather than by where they sit in the DOM.
     */
    refreshMoveControls() {
      const order = this.completeOrder()

      order.forEach((occurrenceId, index) => {
        this.pointNodes(`[data-meeting-move="up"][data-move-point="${occurrenceId}"]`)
          .forEach(button => { button.disabled = index === 0 })
        this.pointNodes(`[data-meeting-move="down"][data-move-point="${occurrenceId}"]`)
          .forEach(button => { button.disabled = index === order.length - 1 })
      })

      this.syncAgendaOrder()
    },

    /**
     * The sortable list itself. Looked up from the stored root, not through
     * `$refs`: the beamer overlay hangs on `body`, and moving a point is exactly
     * what it does from there.
     */
    agendaListElement() {
      return this.editorRoot().querySelector('[data-meeting-agenda-list]') ?? this.$refs?.agendaList ?? null
    },

    completeOrder() {
      const list = this.agendaListElement()
      if (!list) return []

      return Array.from(list.querySelectorAll('[data-meeting-item]'))
        .map(element => Number.parseInt(element.dataset.occurrenceId, 10))
        .filter(Number.isInteger)
    },

    async persistOrder() {
      if (await this.flushInlineContent() === false) return false
      const order = this.completeOrder()
      const wire = this._livewire()

      if (!wire || order.length === 0) {
        this.restoreOrder()
        this.reportError(new Error('meetingEditor: Livewire component not resolvable'))
        return
      }

      this.sorting = true

      try {
        const accepted = await wire.reorderAgenda([...order])
        if (accepted !== true) this.restoreOrder()
      } catch (error) {
        this.restoreOrder()
        this.reportError(error)
      } finally {
        this.sorting = false
        this.refreshAgenda()
      }
    },

    restoreOrder() {
      if (!sortable || orderBeforeDrag.length === 0) return
      sortable.sort(orderBeforeDrag.map(String), true)
    },

    // ===== Inline point editing =====

    /**
     * Move the single rich-text instance into the clicked point. The previous
     * point is flushed first, so switching never drops an edit.
     *
     * `restoreDraft` carries text that was rescued from a morph (see
     * `watchLivewireCommits`): it is newer than anything the server just rendered,
     * so it wins and stays dirty, which is what gets it saved afterwards.
     */
    async openPointContent(occurrenceId, restoreDraft = null) {
      if (this.pointTaskDialogOpen && this.pointTaskOccurrenceId !== occurrenceId) return false
      if (this.inlinePointId === occurrenceId) {
        const slot = this.pointContentSlot(occurrenceId)
        const editor = this.pointEditorElement()

        // Already being written on the surface that is in charge: only the caret
        // moves. Standing on the OTHER surface - the card while the beamer took
        // over, or the other way round - the editor is carried across as it is.
        // Re-reading the stored value here would drop whatever is not saved yet.
        if (!slot || !editor || editor.parentElement !== slot) this.movePointEditorInto(occurrenceId)
        else if (!this.pointTaskDialogOpen) focusRichTextEditor(POINT_CONTENT_EDITOR_ID)

        return
      }

      if (await this.flushInlineContent() === false) return false

      if (this.pointTaskDialogOpen && this.pointTaskOccurrenceId !== occurrenceId) return false
      this.inlinePointId = occurrenceId

      this.$nextTick(() => {
        if (!this.movePointEditorInto(occurrenceId)) {
          this.inlinePointId = null
          return
        }

        updateRichTextEditor(
          POINT_CONTENT_EDITOR_ID,
          typeof restoreDraft === 'string' ? restoreDraft : this.pointContent(occurrenceId),
          this.pointContentPreviews(occurrenceId)
        )
        this.pointTaskCommand('setMeetingTaskContext', String(occurrenceId))
        inlineContentDirty = typeof restoreDraft === 'string'
        if (inlineContentDirty) {
          inlineContentRevision++
          this.scheduleInlineContentFlush()
        }
        if (!this.pointTaskDialogOpen) focusRichTextEditor(POINT_CONTENT_EDITOR_ID)
      })
    },

    pointTaskCommand(command, ...args) {
      const handle = document.querySelector(`[data-rich-text-editor="${POINT_CONTENT_EDITOR_ID}"]`)?.resourcererRichText
      return handle?.extensionCommand?.('meeting-inline-tasks', command, ...args) === true
    },

    receivePointTaskSelection(detail) {
      this.selectedPointTaskToken = detail?.token || ''
      this.pointTaskCanCreate = detail?.canCreate === true
    },

    openPointTask(detail) {
      if (!detail?.token || this.inlinePointId === null || this.pointTaskDialogOpen) return false
      this.pointTaskPeopleError = false
      this.pointTask = { title: '', due: '', mode: 'pending', ...detail, people: [...detail.people] }
      this.pointTaskOriginalMetadata = JSON.stringify([this.pointTask.title, this.pointTask.due, this.pointTask.mode, this.pointTask.people])
      this.pointTaskOccurrenceId = this.inlinePointId
      this.pointTaskDialogOpen = true
      this.$nextTick(() => {
        if (!this.pointTaskDialogOpen) return
        window.dispatchEvent(new CustomEvent('people-picker-synced', {
          detail: { key: 'meeting-inline-task', origin: 0, selected: [...this.pointTask.people], reset: true }
        }))
        this.$nextTick(() => {
          if (!this.pointTaskDialogOpen) return
          const dialog = this.editorRef('pointTaskDialog')
          dialog?.showModal()
          dialog?.querySelector('[autofocus]')?.focus({ preventScroll: true })
        })
      })
      return true
    },

    closePointTask({ restoreFocus = true } = {}) {
      const wasOpen = this.pointTaskDialogOpen
      if (wasOpen) this.pointTaskCommand('cancelMeetingTask', this.pointTask.token)
      this.pointTaskDialogOpen = false
      this.pointTaskPeopleError = false
      this.pointTaskOccurrenceId = null
      this.editorRef('pointTaskDialog')?.close()
      if (wasOpen && restoreFocus && this.inlinePointId !== null) focusRichTextEditor(POINT_CONTENT_EDITOR_ID)
    },

    unmarkPointTask() {
      if (!this.pointTaskDialogOpen) return false
      if (!this.pointTask.isNew && !this.pointTaskCommand('unmarkMeetingTask', this.pointTask.token)) return false
      this.closePointTask()
      return true
    },

    async markPointTask() {
      if (this.pointTaskBusy || this.inlinePointId === null) return false
      const nonce = uuidv4()
      if (!this.pointTaskCommand('prepareMeetingTask', nonce)) return false
      const occurrenceId = this.inlinePointId
      this.pointTaskBusy = true
      try {
        if (!await this.awaitPointEditorUploads()) return false
        const wire = this._livewire()
        const token = await wire.newPointTaskIdentity(occurrenceId)
        if (this.inlinePointId !== occurrenceId || !this.pointTaskCommand('stageMeetingTask', token, nonce)) {
          this.reportError(new Error('meetingEditor: paragraph changed; select it and mark again'))
          return false
        }
        return true
      } catch (error) {
        this.reportError(error)
        return false
      } finally {
        this.pointTaskBusy = false
      }
    },

    updatePointTask(metadata) {
      if (!this.pointTaskDialogOpen || !this.pointTask.token ||
        (this.inlinePointId !== this.pointTaskOccurrenceId && pendingReopenPointId !== this.pointTaskOccurrenceId)) return false
      for (const name of ['people', 'due', 'title', 'mode']) {
        const value = metadata[`data-meeting-task-${name}`]
        if (typeof value === 'string') {
          this.pointTask[name] = name === 'people'
            ? value.split(',').filter(Boolean).map(id => `person.${id}`)
            : value
        }
      }
      return true
    },

    savePointTask(takeoverAvailable = false) {
      if (!this.pointTaskDialogOpen ||
        (this.inlinePointId !== this.pointTaskOccurrenceId && pendingReopenPointId !== this.pointTaskOccurrenceId)) return false
      const task = this.pointTask
      if (!takeoverAvailable && task.people.length === 0) {
        this.pointTaskPeopleError = true
        return false
      }
      if (task.mode === 'takeover' && task.people.length > 0) return false
      if (!task.isNew && this.pointTaskOriginalMetadata === JSON.stringify([task.title, task.due, task.mode, task.people])) {
        this.closePointTask()
        return true
      }
      const metadata = {
        'data-meeting-task-title': task.title,
        'data-meeting-task-people': task.people.map(key => key.replace('person.', '')).join(','),
        'data-meeting-task-due': task.due,
        'data-meeting-task-mode': task.mode
      }
      const saved = task.isNew
        ? this.pointTaskCommand('markMeetingTask', task.token, task.nonce, metadata)
        : this.pointTaskCommand('setMeetingTask', task.token, metadata)
      if (!saved) {
        this.reportError(new Error('meetingEditor: task paragraph changed; cancel and reopen the task dialog'))
        return false
      }
      this.pointTaskCommand('finishMeetingTaskEdit')
      this.closePointTask()
      return true
    },

    updatePointTaskPeople(detail) {
      if (detail?.key !== 'meeting-inline-task' || detail.origin === 0 || !this.pointTaskDialogOpen) return
      this.updatePointTask({
        'data-meeting-task-people': detail.selected.map(key => key.replace('person.', '')).join(',')
      })
      if (this.pointTask.people.length > 0) this.pointTaskPeopleError = false
    },

    /**
     * Put the travelling editor into the slot of that point and focus it, without
     * touching what is written in it. Returns false when there is no slot to move
     * into, which is the one thing the caller has to react to.
     */
    movePointEditorInto(occurrenceId) {
      const slot = this.pointContentSlot(occurrenceId)
      const editor = this.pointEditorElement()

      if (!slot || !editor) {
        this.reportError(new Error('meetingEditor: point content slot not resolvable'))

        return false
      }

      if (editor.parentElement !== slot) slot.appendChild(editor)
      if (!this.pointTaskDialogOpen) focusRichTextEditor(POINT_CONTENT_EDITOR_ID)

      return true
    },

    pointContent(occurrenceId) {
      const raw = this.editorRoot().querySelector(`[data-point-content-value="${occurrenceId}"]`)

      return raw ? (raw.value ?? '') : ''
    },

    /** The package update API requires a Record, including an explicitly empty {}. */
    pointContentPreviews(occurrenceId) {
      const raw = this.editorRoot().querySelector(`[data-point-content-previews="${occurrenceId}"]`)

      if (!raw) return {}

      try {
        const parsed = JSON.parse(raw.value || '{}')

        return parsed !== null && !Array.isArray(parsed) && typeof parsed === 'object'
          ? { ...parsed }
          : {}
      } catch (error) {
        this.reportError(error)

        return {}
      }
    },

    storePointContentPreviews(occurrenceId, previews) {
      const raw = this.editorRoot().querySelector(`[data-point-content-previews="${occurrenceId}"]`)
      // PHP serializes an empty associative array as `[]` in the Livewire
      // response. Adapt that transport-only shape before calling the package,
      // whose public contract deliberately accepts records but never arrays.
      const record = previews !== null && !Array.isArray(previews) && typeof previews === 'object'
        ? { ...previews }
        : {}

      if (raw) raw.value = JSON.stringify(record)

      return record
    },

    /** PHP serializes an empty map-shaped array as []; the package accepts records only. */
    inlineMediaRecord(value) {
      return value !== null && !Array.isArray(value) && typeof value === 'object'
        ? { ...value }
        : {}
    },

    /** Park the editor back outside the list without touching the server. */
    parkPointEditor({ keepContext = false } = {}) {
      const editor = this.pointEditorElement()
      const park = this.editorRef('pointEditorPark')

      if (editor && park && editor.parentElement !== park) park.appendChild(editor)
      if (keepContext) return
      this.inlinePointId = null
      inlineContentDirty = false
      clearTimeout(inlineContentTimer)
      inlineContentTimer = null
    },

    async closePointContent() {
      if (this.pointTaskDialogOpen) return false
      if (await this.flushInlineContent() === false) return false
      if (this.pointTaskDialogOpen) return false
      this.parkPointEditor()
    },

    /**
     * Focus left the point entirely: save what is open and hand the card back to
     * its reading state.
     *
     * The check is deferred on purpose. Opening a point hides the focused reading
     * view and re-parents the editor, and both make the browser fire focusout with
     * an empty relatedTarget before the editor has received focus. Only once the
     * focus has settled can we tell a real leave from that hand-over.
     */
    async leavePoint(occurrenceId) {
      if (this.inlinePointId !== occurrenceId) return
      if (this.pointTaskDialogOpen || this.pointTaskBusy) return

      // The beamer view holds the editor; the card it came from has lost the
      // focus by definition then and must not close what it no longer owns.
      if (this.presentingPointId !== null) return

      await new Promise(resolve => setTimeout(resolve, 150))

      if (this.inlinePointId !== occurrenceId) return
      if (this.pointTaskDialogOpen || this.pointTaskBusy) return

      const card = this.editorRoot().querySelector(`[data-occurrence-id="${occurrenceId}"]`)
      if (card && card.contains(document.activeElement)) return

      await this.closePointContent()
    },

    markInlineContentChanged(event) {
      if (event.detail?.id !== POINT_CONTENT_EDITOR_ID || this.inlinePointId === null) return

      inlineContentDirty = true
      inlineContentRevision++
      this.scheduleInlineContentFlush()
    },

    scheduleInlineContentFlush() {
      clearTimeout(inlineContentTimer)
      inlineContentTimer = setTimeout(() => this.flushInlineContent(), INLINE_IDLE_MS)
    },

    /**
     * Take the pending minutes out of the editor WITHOUT saving them. Returns null
     * when nothing was typed since the last save, so a restore never puts a stale
     * copy over freshly rendered server content.
     */
    captureInlineContent() {
      if (!inlineContentDirty) return null

      syncRichTextEditor(POINT_CONTENT_EDITOR_ID)
      const input = document.getElementById(`${POINT_CONTENT_EDITOR_ID}-input`)

      return input ? input.value : null
    },

    /**
     * Pictures pasted into the minutes are uploaded one at a time while typing
     * continues, and only a finished upload leaves a token in the html. Storing
     * before that would persist a text whose pictures the server cannot adopt -
     * they would simply be gone. The rich-text component already knows when it
     * is ready, so ask it instead of tracking uploads a second time here.
     *
     * Returns false while an upload is still running or has failed; the text
     * then stays dirty and unsaved, and the component shows the reason.
     */
    async awaitPointEditorUploads() {
      const root = document.querySelector(`[data-rich-text-editor="${POINT_CONTENT_EDITOR_ID}"]`)
      if (!root) {
        this.reportError(new Error('meetingEditor: editor is unavailable; draft retained'))
        return false
      }

      try {
        const handle = root.resourcererRichText
        if (!handle || typeof handle.whenReady !== 'function' || await handle.whenReady() === false) {
          this.reportError(new Error('meetingEditor: content is not ready; draft retained'))
          return false
        }
        return true
      } catch (error) {
        this.reportError(error)
        return false
      }
    },

    async flushInlineContent() {
      clearTimeout(inlineContentTimer)
      inlineContentTimer = null

      const occurrenceId = this.inlinePointId
      if (occurrenceId === null || !inlineContentDirty) return

      // A temporary token is one-shot server state. If its request is still in
      // flight, wait for the 0.1.4 reconciliation before taking another snapshot;
      // otherwise the consumed token could be submitted a second time.
      if (inlineTokenSave?.occurrenceId === occurrenceId) {
        await inlineTokenSave.promise

        if (this.inlinePointId === occurrenceId && inlineContentDirty) {
          return this.flushInlineContent()
        }

        return
      }

      if (!await this.awaitPointEditorUploads()) return false

      // Awaiting an upload gives the user time to move on; the point that was
      // being written may no longer be the open one.
      if (this.inlinePointId !== occurrenceId) return

      syncRichTextEditor(POINT_CONTENT_EDITOR_ID)
      const input = document.getElementById(`${POINT_CONTENT_EDITOR_ID}-input`)
      const value = input ? input.value : ''
      const request = {
        occurrenceId,
        requestId: ++inlineContentRequestSequence,
        revision: inlineContentRevision
      }

      latestInlineContentRequestId = request.requestId
      inlineContentDirty = false

      const saving = this.persistInlineContent(request, value)

      if (value.includes('data-rich-text-upload-token')) {
        inlineTokenSave = { occurrenceId, promise: saving }
      }

      try {
        return await saving
      } finally {
        if (inlineTokenSave?.promise === saving) inlineTokenSave = null
      }
    },

    async persistInlineContent(request, value) {
      const accepted = await this.saveInlineField(
        request.occurrenceId,
        'content',
        value,
        request
      )

      if (!accepted || accepted.ignored) {
        if (
          !accepted?.ignored &&
          this.inlinePointId === request.occurrenceId &&
          inlineContentRevision === request.revision
        ) {
          inlineContentDirty = true
        }

        return false
      }

      const content = typeof accepted.content === 'string' ? accepted.content : value
      const previews = this.storePointContentPreviews(
        request.occurrenceId,
        accepted.inlineMediaPreviews
      )
      const references = this.inlineMediaRecord(accepted.temporaryUploadReferences)
      const raw = this.editorRoot().querySelector(
        `[data-point-content-value="${request.occurrenceId}"]`
      )
      if (raw) raw.value = content

      if (this.inlinePointId === request.occurrenceId) {
        const hasReferences = Object.keys(references).length > 0

        if (hasReferences) {
          reconcileRichTextInlineMedia(POINT_CONTENT_EDITOR_ID, references, previews)
        } else {
          // No token changed, but deleting an image may still have removed a
          // preview grant. Replace the map against the editor's CURRENT html,
          // never against the older submitted snapshot.
          syncRichTextEditor(POINT_CONTENT_EDITOR_ID)
          const current = document.getElementById(`${POINT_CONTENT_EDITOR_ID}-input`)
          updateRichTextEditor(POINT_CONTENT_EDITOR_ID, current?.value ?? content, previews)
        }
      }

      this.paintPointContent(request.occurrenceId, accepted)
      return true
    },

    /**
     * The inline save skips the Livewire morph on purpose, so the closed card
     * would keep showing the text it was rendered with. Paint the accepted
     * content in - the server's sanitized html, so what is on screen is exactly
     * what a reload would render.
     */
    paintPointContent(occurrenceId, result) {
      if (typeof result?.contentHtml !== 'string') return

      const displays = this.pointNodes(`[data-point-content-display="${occurrenceId}"]`)

      if (displays.length === 0) {
        this.reportError(new Error('meetingEditor: point content display not resolvable'))
        return
      }

      displays.forEach(display => {
        display.innerHTML = result.contentHtml
        display.classList.toggle('hidden', result.hasContent === false)
      })
      this.pointNodes(`[data-point-content-empty="${occurrenceId}"]`)
        .forEach(empty => { empty.classList.toggle('hidden', result.hasContent !== false) })
    },

    /**
     * Autosave races every other control on the page: a click tears focus out of
     * the editor - or the page down entirely - before a blur driven save can
     * finish, and `beforeunload` cannot help because it may not await anything.
     *
     * The answer sits one level BELOW the buttons instead of inside them. Every
     * click is caught in the CAPTURE phase, before any Alpine or Livewire handler
     * sees it; the pending edits are flushed; and only then is the very same click
     * replayed on its original target. Whatever it was about to do - a Livewire
     * roundtrip, a dialog, a navigation - then happens against a server that
     * already has the typed text. A button added tomorrow is covered without
     * knowing anything about it, which is exactly what a list of call sites cannot
     * promise.
     *
     * Rejected alternatives: `Livewire.interceptAction` is synchronous and the
     * inline save is a request of its own, so nothing can be awaited there;
     * flushing at each call site alone makes the next new button a data loss
     * again. Only `click` is held back, never `mousedown` - focus and caret
     * placement have already happened by then, so nothing the user sees moves.
     *
     * The editor once ran an `interceptAction` hook as well, but it belonged to
     * the removed point form: it flushed THAT form's rich-text instance and
     * queued its client side dirty flag. It never touched the travelling editor,
     * which for the reason above it could not have saved anyway. What protects
     * the typed minutes is this guard plus `watchLivewireCommits()`, and both are
     * unchanged.
     */
    guardPendingEdits() {
      const onClick = async(event) => {
        if (event === replayedClick) return
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
        ) return

        const target = event.target
        if (!(target instanceof Element)) return

        const link = target.closest?.('a[href]')
        const navigating = !!link &&
          (!link.target || link.target === '_self') &&
          !link.getAttribute('href')?.startsWith('#')

        // The editor that owns the pending text keeps working untouched: its
        // toolbar and its own text area are how that text is written.
        if (!navigating && this.isInsideOwnEditor(target)) return
        if (!this.hasPendingEdits()) return

        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()

        if (await this.flushPendingEdits() === false) return false

        if (navigating) {
          if (link.hasAttribute('wire:navigate') && window.Livewire?.navigate) {
            window.Livewire.navigate(link.href)
          } else {
            window.location.assign(link.href)
          }

          return
        }

        this.replayClick(event)
      }

      // Backgrounding a tab is the other way an edit gets lost, and on mobile it
      // is the common one.
      const onHidden = () => {
        if (document.visibilityState === 'hidden' && this.hasPendingEdits()) this.flushPendingEdits()
      }

      document.addEventListener('click', onClick, true)
      document.addEventListener('visibilitychange', onHidden)

      stopGuardingEdits = () => {
        document.removeEventListener('click', onClick, true)
        document.removeEventListener('visibilitychange', onHidden)
      }
    },

    isInsideOwnEditor(target) {
      const editor = this.pointEditorElement()
      if (editor?.contains?.(target)) return true
      if (this.pointTaskDialogOpen && this.editorRef('pointTaskDialog')?.contains(target)) return true

      const active = document.activeElement

      return !!(active && active.isContentEditable && active.contains(target))
    },

    /**
     * Replay the held back click on its original target. The event is remembered
     * by identity, so the guard lets exactly this one through instead of holding
     * it back a second time and flushing twice.
     */
    replayClick(event) {
      const target = event.target

      // The flush is asynchronous. A target that vanished meanwhile cannot be
      // clicked any more, and re-targeting would act on the wrong control.
      if (!target?.isConnected) return

      // No `view`: the pointer geometry is what a handler may read back, while a
      // Window from another realm makes the constructor throw outright.
      const replay = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        composed: true,
        detail: event.detail,
        screenX: event.screenX,
        screenY: event.screenY,
        clientX: event.clientX,
        clientY: event.clientY,
        button: event.button,
        buttons: event.buttons
      })

      replayedClick = replay

      try {
        target.dispatchEvent(replay)
      } finally {
        replayedClick = null
      }
    },

    hasPendingEdits() {
      if (inlineContentDirty || this.inlineSaving) return true

      // A focused plain contenteditable may hold keystrokes that only its idle
      // timer or its blur stores. A rich-text editable is not one of those: the
      // travelling point editor tracks its own dirty flag above, and the item
      // form persists on submit - guarding those would cost focus for nothing.
      return this.pendingPlainEditable() !== null
    },

    pendingPlainEditable() {
      const active = document.activeElement
      const pending = !!(
        active &&
        active.isContentEditable &&
        this.editorRoot().contains(active) &&
        !active.closest?.('[data-rich-text-editor]')
      )

      return pending ? active : null
    },

    async flushPendingEdits() {
      // Blurring hands a plain contenteditable to its own save path; the rich
      // text is flushed explicitly because it keeps its value outside the DOM.
      this.pendingPlainEditable()?.blur?.()

      if (await this.flushInlineContent() === false) return false

      const deadline = Date.now() + 3000
      while (this.inlineSaving && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 50))
      }
      return !inlineContentDirty && !this.inlineSaving
    },

    // ===== Beamer view =====

    /**
     * Show one point full screen so the room can read along while it is being
     * minuted. The overlay is no second editor: it offers the same
     * `data-point-content-slot` a card does, so the single travelling rich-text
     * instance simply moves over there - with its autosave, its click guard and
     * its rescue across a morph unchanged.
     */
    async startPresenting(occurrenceId) {
      if (this.presentingPointId === occurrenceId) return

      // The overlay goes up first, so the room never looks at a black screen
      // while the refresh below is on its way.
      //
      // Real fullscreen is NOT asked for here: the overlay already covers the
      // page, and whoever starts the mode usually still has to arrange things -
      // pick the projector's window, move it across, come back. Being thrown out
      // of the browser for that is worse than one more click on the button in the
      // head of the stage, which is a gesture of its own and may therefore ask.
      this.presentingPointId = occurrenceId
      // Decided here and not at page load: whether there is room for the list
      // beside the stage is a question about the screen the mode runs on, and a
      // projector is usually plugged in after the page was opened.
      this.presentationSidebarOpen = this.storedPresentationSidebarOpen()
      this.syncPresentationChrome()
      this.applyPresentationFontScale()
      this.syncAgendaOrder()

      await this.refreshForPresentation()
      await this.openPointContent(occurrenceId)
    },

    /**
     * A card keeps its own head in step after an inline save, which skips the
     * render on purpose; the beamer states the same head from the server and
     * would otherwise show what the page was loaded with. One refresh on entering
     * settles heading, status, responsibility, deadline and the agenda order at
     * once - and nothing but the minutes can change from inside the mode, so it
     * is asked for exactly here and never again while the meeting runs.
     */
    async refreshForPresentation() {
      if (await this.flushInlineContent() === false) return false

      const wire = this._livewire()

      if (!wire || typeof wire.$refresh !== 'function') return

      try {
        await wire.$refresh()
      } catch (error) {
        // A stale heading is the whole cost; the mode itself still opens.
        this.reportError(error)
      }
    },

    /**
     * Back to the list. What was typed is stored first, and the editor returns
     * into the card of the very point that was on the beamer, so writing simply
     * continues where the room stopped watching.
     */
    async stopPresenting() {
      const occurrenceId = this.presentingPointId
      if (occurrenceId === null) return

      // Cleared before anything is awaited: leaving through the browser's own
      // fullscreen exit reaches this twice, and the second call must find
      // nothing left to do.
      this.presentingPointId = null
      this.syncAgendaOrder()
      this.syncPresentationChrome()
      this.exitPresentationFullscreen()

      await this.closePointContent()

      this.$nextTick(() => {
        if (this.pointContentSlot(occurrenceId)) this.openPointContent(occurrenceId)
      })
    },

    /**
     * A point that comes up while the room is looking at the stage.
     *
     * It is inserted directly AFTER the point being shown - that is where a
     * meeting puts what has just come up - and the stage moves on to it at once,
     * with the heading ready to be typed over. Deliberately no draft card here:
     * on a projector there is one point on screen, and a second, dashed card
     * beside it is a state the room would have to interpret. The point exists
     * from the first click and carries the neutral placeholder heading until
     * somebody writes the real one, exactly as the list's draft does when it is
     * abandoned mid-word.
     */
    async addPointOnStage() {
      if (this.presentingPointId === null || this.stageAddingPoint) return false

      const anchor = this.presentingPointId

      // Whatever stands in the travelling editor belongs to the point the room
      // was looking at, not to the new one.
      if (await this.flushInlineContent() === false) return false

      const wire = this._livewire()

      if (!wire || typeof wire.createInlineItem !== 'function') {
        this.reportError(new Error('meetingEditor: Livewire component not resolvable'))

        return false
      }

      this.stageAddingPoint = true

      try {
        const result = await wire.createInlineItem(anchor, 'title', '')

        if (!result?.ok || !result.occurrence_id) {
          this.reportError(new Error('meetingEditor: point on stage rejected'), result?.message)

          return false
        }

        this.presentingPointId = result.occurrence_id
        this.syncAgendaOrder()
        await this.openPointContent(result.occurrence_id)
        this.$nextTick(() => this.selectStageTitle(result.occurrence_id))

        return true
      } catch (error) {
        this.reportError(error)

        return false
      } finally {
        this.stageAddingPoint = false
      }
    },

    /**
     * Put the caret in the heading of the point on stage and select what stands
     * there: the placeholder is meant to be typed over, not edited around.
     */
    selectStageTitle(occurrenceId) {
      const title = document.querySelector(
        `[data-meeting-presentation] [data-testid="presentation-point-title-${occurrenceId}"]`
      )

      if (!title) return

      title.focus()

      const selection = window.getSelection?.()

      if (!selection || typeof document.createRange !== 'function') return

      const range = document.createRange()
      range.selectNodeContents(title)
      selection.removeAllRanges()
      selection.addRange(range)
    },

    /**
     * One point forward or back along the agenda, read from the rendered list so
     * a reorder is followed without asking the server. There is deliberately no
     * wrap around: jumping from the last point back to the first would look like
     * one more point and silently start the meeting over.
     */
    async presentAdjacent(offset, event = null, respectCaret = true) {
      if (this.pointTaskDialogOpen) return false
      if (this.presentingPointId === null) return false
      // Inside the minutes an arrow key moves the caret, and it must keep doing
      // exactly that - the buttons are what turns the page there. The page keys a
      // presenter remote sends mean nothing else anywhere, so they turn the page
      // even while somebody is writing: the stage opens the minutes of the point
      // it shows, so the caret is usually IN them, and a clicker that only worked
      // as long as nobody had typed would be no clicker at all.
      if (event && respectCaret && !this.presentationShortcutAllowed(event)) return false

      const order = this.completeOrder()
      const index = order.indexOf(this.presentingPointId)
      const target = index + offset

      if (index === -1 || target < 0 || target >= order.length) return false

      event?.preventDefault?.()
      this.presentingPointId = order[target]
      this.syncAgendaOrder()
      await this.openPointContent(order[target])

      return true
    },

    /**
     * Which point the stage moves to when the one it was showing was removed: the
     * NEXT one along the agenda, and the one before it when the removed point was
     * the last. Read from the order as it stood before the removal, because the
     * removed point no longer has a place to count from.
     *
     * @param {number|null} removedId
     * @param {number[]} order
     */
    presentationSuccessor(removedId, order) {
      const index = Array.isArray(order) ? order.indexOf(removedId) : -1

      if (removedId === null || index === -1) return null

      const after = order.slice(index + 1).find(id => this.pointOnAgenda(id))
      if (after !== undefined) return after

      const before = order.slice(0, index).reverse().find(id => this.pointOnAgenda(id))

      return before === undefined ? null : before
    },

    /** Whether there is a point in that direction - the buttons say so as well. */
    canPresentAdjacent(offset) {
      if (this.presentingPointId === null) return false

      const order = this.agendaOrder
      const index = order.indexOf(this.presentingPointId)
      const target = index + offset

      return index !== -1 && target >= 0 && target < order.length
    },

    /**
     * "Point 3 of 7" - where the room stands in the agenda. Spelled out rather
     * than left as a fraction: on the stage there is no list beside it that would
     * explain what the two numbers mean.
     */
    presentationPosition() {
      const order = this.completeOrder()
      const index = order.indexOf(this.presentingPointId)

      return index === -1
        ? ''
        : this.positionLabel.replace(':current', index + 1).replace(':total', order.length)
    },

    /**
     * How large the stage sets its type. It is a property of the room and the
     * projector, so the choice is remembered in this browser and applies to the
     * stage alone - the agenda list keeps the size everything else on the page
     * has. One scale for the whole stage: the point states its sizes in `em`, so
     * heading, minutes and meta line move together instead of drifting apart.
     */
    changePresentationFontScale(steps) {
      const raw = this.presentationFontScale + steps * PRESENTATION_SCALE_STEP

      this.presentationFontScale = this.clampPresentationFontScale(raw)
      this.storePresentationFontScale()
      this.applyPresentationFontScale()
    },

    clampPresentationFontScale(value) {
      const rounded = Math.round(value * 10) / 10

      return Math.min(PRESENTATION_SCALE_MAX, Math.max(PRESENTATION_SCALE_MIN, rounded))
    },

    /**
     * A morph rewrites the overlay, and an inline style is not part of what the
     * server renders - so the chosen size is written back onto the stage rather
     * than being set once.
     */
    applyPresentationFontScale() {
      this.presentationRoot()?.style?.setProperty?.(
        '--meeting-presentation-scale',
        String(this.presentationFontScale)
      )
    },

    storedPresentationFontScale() {
      try {
        const stored = Number.parseFloat(window.localStorage?.getItem(PRESENTATION_SCALE_KEY) ?? '')

        return Number.isFinite(stored) ? this.clampPresentationFontScale(stored) : 1
      } catch {
        // A browser that refuses storage simply starts at the designed size.
        return 1
      }
    },

    storePresentationFontScale() {
      try {
        window.localStorage?.setItem(PRESENTATION_SCALE_KEY, String(this.presentationFontScale))
      } catch {
        // Nothing to recover from: the size still applies to this run.
      }
    },

    /**
     * Jump straight to a point from the agenda beside the stage. Same road as
     * paging: only the shown point changes, the travelling editor moves with it.
     */
    async presentPoint(occurrenceId) {
      if (this.pointTaskDialogOpen) return false
      if (this.presentingPointId === null || this.presentingPointId === occurrenceId) return

      this.presentingPointId = occurrenceId
      this.syncAgendaOrder()
      await this.openPointContent(occurrenceId)
    },

    togglePresentationSidebar() {
      this.presentationSidebarOpen = !this.presentationSidebarOpen
      this.storePresentationSidebarOpen()
    },

    /**
     * On a wide screen the agenda is there from the start; on a narrow one it
     * would take the stage's room, so it waits to be asked for. A choice that was
     * made once wins over both.
     */
    storedPresentationSidebarOpen() {
      try {
        const stored = window.localStorage?.getItem(PRESENTATION_SIDEBAR_KEY)

        if (stored === '1' || stored === '0') return stored === '1'
      } catch {
        // A browser that refuses storage simply follows the screen width.
      }

      return (window.innerWidth ?? 0) >= PRESENTATION_SIDEBAR_MIN_WIDTH
    },

    storePresentationSidebarOpen() {
      try {
        window.localStorage?.setItem(PRESENTATION_SIDEBAR_KEY, this.presentationSidebarOpen ? '1' : '0')
      } catch {
        // Nothing to recover from: the choice still applies to this run.
      }
    },

    presentationShortcutAllowed(event) {
      const target = event?.target

      if (!(target instanceof Element)) return true
      if (this.isInsideOwnEditor(target)) return false

      return target.closest?.('input, textarea, select, [contenteditable="true"]') === null
    },

    /**
     * Real fullscreen is a bonus, never a requirement: the overlay already covers
     * the page. A browser that refuses - an embedded webview, a missing
     * permission - simply keeps the overlay, and the presenter reads no error
     * about it.
     */
    requestPresentationFullscreen() {
      // The DOCUMENT goes fullscreen, not the overlay: a browser paints nothing
      // outside the fullscreen element, and everything the app puts over the page
      // - the confirmation dialog of a deletion, a failed autosave's toast -
      // lives beside the overlay, not inside it. Asking for the overlay alone
      // made a delete confirmation impossible to answer in front of the room.
      // The overlay covers the viewport either way, so nothing about the picture
      // changes.
      const root = document.documentElement

      if (!root || typeof root.requestFullscreen !== 'function' || document.fullscreenElement) return

      try {
        Promise.resolve(root.requestFullscreen()).then(
          () => {
            this.presentationFullscreen = true
            // Granted only after the mode was already left: leave fullscreen
            // again instead of stranding the browser in it.
            if (this.presentingPointId === null) this.exitPresentationFullscreen()
          },
          () => { this.presentationFullscreen = false }
        )
      } catch {
        this.presentationFullscreen = false
      }
    },

    exitPresentationFullscreen() {
      if (!this.presentationFullscreen) return

      this.presentationFullscreen = false

      if (!document.fullscreenElement || typeof document.exitFullscreen !== 'function') return

      try {
        Promise.resolve(document.exitFullscreen()).catch(() => {})
      } catch {
        // Nothing to recover from: the overlay is closed either way.
      }
    },

    togglePresentationFullscreen() {
      if (this.presentationFullscreen) this.exitPresentationFullscreen()
      else this.requestPresentationFullscreen()
    },

    /**
     * Escape inside real fullscreen is swallowed by the browser, which drops out
     * of fullscreen on its own. Without this the overlay would stay up in a state
     * nobody asked for, so it follows the browser out.
     */
    watchFullscreenExit() {
      const onChange = () => {
        if (document.fullscreenElement || !this.presentationFullscreen) return

        this.presentationFullscreen = false
        this.stopPresenting()
      }

      document.addEventListener('fullscreenchange', onChange)
      stopWatchingFullscreen = () => document.removeEventListener('fullscreenchange', onChange)
    },

    // ===== Inline classification and assignment (stage 2) =====

    openPointMenu: null,
    openPointAssignment: null,
    // Anchor of the client side draft card, null when no draft is open.
    draftAfterId: null,
    draftSaving: false,

    /**
     * Turn a virtual open point into a real agenda point of this meeting because
     * something was changed about it here. The server appends it to the agenda and
     * re-renders, so the card comes back as an ordinary point.
     */
    async adoptOpenPoint(itemId, field, value = null, { openContent = false } = {}) {
      if (await this.flushInlineContent() === false) return false
      const wire = this._livewire()

      if (!wire || typeof wire.adoptOpenPoint !== 'function') {
        this.reportError(new Error('meetingEditor: Livewire component not resolvable'))
        return false
      }

      if (this.adoptingItemId !== null) return false

      this.adoptingItemId = itemId
      this.inlineSaving = true

      try {
        const result = await wire.adoptOpenPoint(itemId, field, value)

        if (!result?.ok) {
          this.reportError(new Error('meetingEditor: open point not adopted'), result?.message)
          return false
        }

        this.flashInlineSaved()

        const occurrenceId = result.occurrence_id

        if (openContent && occurrenceId) {
          this.$nextTick(() => {
            if (this.pointContentSlot(occurrenceId)) this.openPointContent(occurrenceId)
          })
        }

        return true
      } catch (error) {
        this.reportError(error)

        return false
      } finally {
        this.adoptingItemId = null
        this.inlineSaving = false
      }
    },

    togglePointMenu(occurrenceId) {
      this.openPointMenu = this.openPointMenu === occurrenceId ? null : occurrenceId
    },

    /**
     * Close the classification menu when the click went somewhere else.
     *
     * "Somewhere else" has to mean outside BOTH copies of the point: the agenda
     * list and the beamer stage render the same point, and which menu is open is
     * one piece of state. Without this check the badge on the stage opened the
     * menu and the very same click closed it again - through the `click.outside`
     * of the copy in the list.
     */
    closePointMenuOnOutside(occurrenceId, event) {
      if (this.openPointMenu !== occurrenceId) return
      if (event?.target?.closest?.(`[data-classification-anchor="${occurrenceId}"]`)) return

      this.openPointMenu = null
    },

    togglePointAssignment(occurrenceId) {
      this.openPointAssignment = this.openPointAssignment === occurrenceId ? null : occurrenceId
    },

    /**
     * Both controls repaint themselves from the chosen option instead of asking
     * Livewire to re-render - a morph here would flicker and tear down the open
     * panel while the user is still working in it.
     */
    // ===== Draft point =====

    /**
     * The insert handle only opens a card in the browser. Nothing is stored until
     * the first real input arrives, so an abandoned draft leaves no empty point.
     */
    startDraftPoint(afterOccurrenceId) {
      this.draftAfterId = afterOccurrenceId
      this.draftSaving = false
      this.$nextTick(() => {
        // Every insert handle renders a draft card of its own and all but one are
        // hidden. Without scoping the lookup to the opened anchor the focus lands
        // in the invisible draft at the top of the list instead of the new point.
        this.editorRoot()
          .querySelector(`[data-draft-after="${afterOccurrenceId}"] [data-testid="draft-point-title"]`)
          ?.focus?.()
      })
    },

    discardDraftPoint() {
      this.draftAfterId = null
    },

    /** First input on a draft: create the point and let the server render it. */
    async commitDraftPoint(field, value) {
      const anchor = this.draftAfterId
      if (anchor === null || this.draftSaving) return false

      if (await this.flushInlineContent() === false) return false
      const wire = this._livewire()

      if (!wire || typeof wire.createInlineItem !== 'function') {
        this.reportError(new Error('meetingEditor: Livewire component not resolvable'))
        return false
      }

      // The draft stays on screen while it is being stored: hiding it up front
      // left a visible gap until the real card arrived with the response.
      this.draftSaving = true

      try {
        const result = await wire.createInlineItem(anchor, field, value)

        if (!result?.ok) {
          this.reportError(new Error('meetingEditor: draft point rejected'), result?.message)
          return false
        }

        // Only now - the response already morphed the real card into the list.
        this.draftAfterId = null

        return true
      } catch (error) {
        this.reportError(error)

        return false
      } finally {
        this.draftSaving = false
      }
    },

    async choosePointClassification(occurrenceId, value, label, classes) {
      this.openPointMenu = null

      const badges = this.pointNodes(`[data-classification-badge="${occurrenceId}"]`)
      const previous = badges.map(badge => ({ badge, text: badge.textContent, className: badge.className }))

      badges.forEach(badge => {
        badge.textContent = label
        badge.className = `${badge.dataset.baseClass} ${classes}`
      })

      // The menu renders its mark server side, and this save skips the morph on
      // purpose - so the chosen entry is marked here, exactly like the badge.
      const previousMenu = this.paintClassificationMenu(occurrenceId, value)

      const accepted = await this.saveInlineField(occurrenceId, 'classification', value)

      if (accepted) return

      previous.forEach(({ badge, text, className }) => {
        badge.textContent = text
        badge.className = className
      })

      this.restoreClassificationMenu(previousMenu)
    },

    /**
     * Mark the chosen entry in the classification menu of that point. The class
     * strings stay in Blade - they are handed over on the menu element, so the
     * markup keeps one single definition of what marked and unmarked look like.
     *
     * Returns what the entries looked like before, so a refused save can put the
     * mark back where it was.
     */
    paintClassificationMenu(occurrenceId, value) {
      const menus = this.pointNodes(`[data-classification-menu="${occurrenceId}"]`)
      if (menus.length === 0) return null

      const previous = []

      menus.forEach(menu => {
        const base = menu.dataset.optionBaseClass ?? ''
        const active = menu.dataset.activeClass ?? ''
        const inactive = menu.dataset.inactiveClass ?? ''

        Array.from(menu.querySelectorAll('[data-classification-option]')).forEach(option => {
          previous.push({ option, className: option.className })
          const marked = option.dataset.classificationOption === String(value)
          option.className = `${base} ${marked ? active : inactive}`
        })
      })

      return previous
    },

    restoreClassificationMenu(previous) {
      if (!Array.isArray(previous)) return

      previous.forEach(({ option, className }) => { option.className = className })
    },

    async savePointAssignment(occurrenceId, field, value) {
      const accepted = await this.saveInlineField(occurrenceId, field, value)

      if (accepted && field === 'due_date') this.refreshAssignmentMarker(occurrenceId, value)
    },

    /**
     * Tell the due-date icon and the meta line of that point what was stored. The
     * responsibility next to it has its own picker and its own
     * `point-responsibilities-changed` event, so it is not touched here.
     *
     * The stored value is passed in rather than read back out of the DOM: this
     * runs from the date picker, whose own `x-data` is what `$root` resolves to
     * there, so a lookup from the component root never found the input at all.
     */
    refreshAssignmentMarker(occurrenceId, dueValue) {
      const due = typeof dueValue === 'string' ? dueValue : ''

      window.dispatchEvent(new CustomEvent('point-assignment-changed', {
        detail: {
          occurrenceId,
          hasDue: due !== '',
          dueLabel: this.formatDueDate(due)
        }
      }))
    },

    /** Render the due date the way the rest of the page does, without a roundtrip. */
    formatDueDate(value) {
      if (!value) return ''

      const parsed = new Date(`${value}T00:00:00`)
      if (Number.isNaN(parsed.getTime())) return value

      return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
        day: '2-digit', month: '2-digit', year: 'numeric'
      }).format(parsed)
    },

    /**
     * Persist one inline field. Returns false when the server refused it, so the
     * calling control can roll its visible value back.
     */
    async saveInlineField(occurrenceId, field, value, inlineRequest = null) {
      // Every inline write funnels through here, so this is the one place that can
      // promise the open minutes are stored before anything else about a point
      // changes - a classification, a deadline, a heading. The content flush is
      // the one caller that must not re-enter.
      if (field !== 'content' && await this.flushInlineContent() === false) return false

      const wire = this._livewire()

      if (!wire || typeof wire.saveItemField !== 'function') {
        this.reportError(new Error('meetingEditor: Livewire component not resolvable'))
        return false
      }

      inlineSaveRequestCount++
      this.inlineSaving = true
      // Announced on the window so the status line does not depend on the Alpine
      // scope chain of whichever inline control triggered the save.
      window.dispatchEvent(new CustomEvent('meeting-point-saving', { detail: { occurrenceId } }))

      try {
        const version = typeof wire.$get === 'function' ? wire.$get('agendaVersion') : ''
        const result = await wire.saveItemField(occurrenceId, field, value, version ?? '')

        // Promise order is not authority. Once a newer content request exists,
        // an older response has no permission to repaint, replace preview grants,
        // emit success or refresh a now unrelated editor.
        if (
          inlineRequest !== null &&
          inlineRequest.requestId !== latestInlineContentRequestId
        ) {
          return { ignored: true }
        }

        if (result?.stale) {
          this.reportError(new Error('meetingEditor: agenda changed elsewhere'), result.message)
          await wire.$refresh()
          return false
        }

        if (!result?.ok) {
          this.reportError(new Error('meetingEditor: inline save rejected'), result?.message)
          return false
        }

        this.flashInlineSaved()
        window.dispatchEvent(new CustomEvent('meeting-point-saved', { detail: { occurrenceId } }))
        // The same point is on screen twice - in the list and on the stage - and
        // this save skipped the morph. Tell the copy that was not edited what is
        // stored now; the field that has the focus keeps what is typed in it.
        window.dispatchEvent(new CustomEvent('meeting-point-field-changed', {
          detail: { occurrenceId, field, value }
        }))

        // The whole payload travels back: a content save carries the sanitized
        // html the card has to show. Callers that only need a yes/no still work,
        // the object is truthy.
        return result
      } catch (error) {
        this.reportError(error)

        return false
      } finally {
        inlineSaveRequestCount = Math.max(0, inlineSaveRequestCount - 1)
        this.inlineSaving = inlineSaveRequestCount > 0
        window.dispatchEvent(new CustomEvent('meeting-point-save-settled', { detail: { occurrenceId } }))
      }
    },

    flashInlineSaved() {
      this.inlineSavedFlash = true
      setTimeout(() => { this.inlineSavedFlash = false }, 1500)
    },

    /**
     * Any OTHER Livewire request morphs the agenda, so the travelling editor is
     * parked before the payload compiles and moved back once the DOM settled.
     */
    watchLivewireCommits() {
      stopWatchingCommits?.()
      stopWatchingCommits = null

      if (typeof window.Livewire?.hook !== 'function') return

      stopWatchingCommits = window.Livewire.hook('commit', ({ component, commit, succeed }) => {
        if (component?.id !== wireId) return

        // These answer with skipRender(), so nothing morphs: the editor must keep
        // its place, its caret and whatever is typed into it. Parking it here
        // would drop the pending text - parkPointEditor() does not flush.
        const calls = Array.isArray(commit?.calls) ? commit.calls : []
        if (calls.length > 0 && calls.every(call => MORPHLESS_CALLS.includes(call?.method))) return

        // A dialog request can morph the component before either the travelling
        // editor or the stage is open; return the one toolbar node before that
        // morph as well, then rediscover it afterwards. The ordinary Livewire DOM
        // rematerialises it reliably; an Alpine teleport template did not rerun
        // after FilePond closed and consumed the only toolbar.
        this.restorePointTools()

        if (this.inlinePointId === null && this.presentingPointId === null) {
          succeed(() => this.$nextTick(() => this.syncAgendaOrder()))

          return
        }

        // Last line of defence. The gesture guard normally stored the text long
        // before a morphing request was compiled, but a programmatic call, a
        // server dispatched refresh or a poll never passes through a click. This
        // hook is synchronous while the inline save is a request of its own, so
        // nothing can be saved from here - the typed text is taken out of the
        // editor instead and put back once the new markup is in place. Nothing is
        // written, so this can never overwrite a newer stored value.
        pendingReopenPointId = this.inlinePointId
        pendingReopenContent = this.captureInlineContent()
        // One more variable on the very same rescue, so the beamer comes back on
        // the point it was showing instead of on whatever the morph rendered.
        pendingPresentingPointId = this.presentingPointId
        // Where the presented point stood. After the morph a removed point is
        // gone from the DOM, and only this order still knows what came after it.
        pendingAgendaOrder = this.completeOrder()
        // The stage is outside the Livewire root. Return its one travelling
        // toolbar before the response morphs the agenda, just like the travelling
        // rich-text editor is parked on the next line.
        const heldByTaskDialog = this.pointTaskDialogOpen
        this.parkPointEditor({ keepContext: heldByTaskDialog })

        succeed(() => this.$nextTick(() => {
          const reopen = pendingReopenPointId
          const draft = pendingReopenContent
          const presenting = pendingPresentingPointId
          const order = pendingAgendaOrder
          pendingReopenPointId = null
          pendingReopenContent = null
          pendingPresentingPointId = null
          pendingAgendaOrder = []

          // A point the request removed has no card left in the agenda; there is
          // nothing to restore the draft into, and writing it back would recreate
          // text for a point this meeting no longer records.
          const survives = this.pointOnAgenda(reopen)
          // The stage follows the agenda instead of closing: whoever just deleted
          // a point in front of the room wants to carry on with the meeting, not
          // to look for the projector again.
          //
          // What the stage shows RIGHT NOW wins over what it showed before the
          // request, as long as that point is still on the agenda: adding a point
          // from the stage moves on to it while the response is still on its way,
          // and restoring the point captured before the morph would undo exactly
          // that move. Only where the current point is gone - it was just deleted
          // - does the captured one decide where the meeting carries on.
          const stage = this.pointOnAgenda(this.presentingPointId)
            ? this.presentingPointId
            : (this.pointOnAgenda(presenting) ? presenting : this.presentationSuccessor(presenting, order))

          this.presentingPointId = stage
          this.syncPresentationChrome()
          this.applyPresentationFontScale()
          this.syncAgendaOrder()

          if (heldByTaskDialog && survives && this.inlinePointId === reopen) {
            // The same connected editor kept its document/history while parked.
            // Metadata may have changed during the request; never reload an older snapshot.
            this.movePointEditorInto(reopen)
            return
          }
          if (!survives && this.pointTaskDialogOpen) this.closePointTask({ restoreFocus: false })
          if (survives) {
            this.openPointContent(reopen, draft)
          } else if (stage !== null) {
            // The point that was being written is gone; the stage still needs the
            // travelling editor in the point it now shows.
            this.openPointContent(stage)
          }
        }))
      })
    },

    reportError(error, message = this.errorMessage) {
      window.dispatchEvent(new CustomEvent('notify', {
        detail: { type: 'danger', title: this.errorTitle, message }
      }))
      Sentry.captureException(error)
      console.error(error)
    }
  }
}
